import { useT } from '@/shared/lib/i18n';
import { EvidenceTape } from '../EvidenceTape';
import { BackArrow } from '../icons/BackArrow';
import { Postit } from '../Postit';
import { RoomCodePostit } from './RoomCodePostit';

export type DossierTopBarProps = {
  /** Back-button handler — when omitted, no back chip renders. */
  onBack?: () => void;
  onShowRules: () => void;
  /** Render a yellow code postit between the rules postit and the tape (online lobby). */
  code?: string;
  /** Mask the code as dots. Host-only toggle drives this. */
  codeHidden?: boolean;
  /** When provided, the code postit shows an eye toggle (host gate). */
  onToggleCodeHidden?: () => void;
  /** Tape label override; defaults to `briefing.tape.briefing_room`. */
  tapeLabel?: string;
};

/**
 * Cork-stage top bar shared by the standalone setup and the online
 * lobby. Back chip on the left, pink "rules" postit, optional yellow
 * code postit (online only), then the BRIEFING ROOM evidence tape on
 * the right.
 */
export const DossierTopBar = ({
  onBack,
  onShowRules,
  code,
  codeHidden,
  onToggleCodeHidden,
  tapeLabel,
}: DossierTopBarProps) => {
  const t = useT();
  return (
    <header className="relative flex h-16 items-center gap-4">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label={t('nav.back')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[6px] border-[1.5px] border-[var(--color-paper)]/55 bg-[rgba(255,243,223,0.10)] text-[var(--color-paper)] transition hover:bg-[rgba(255,243,223,0.22)]"
        >
          <BackArrow />
        </button>
      ) : (
        <a
          href="/"
          aria-label={t('nav.back')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[6px] border-[1.5px] border-[var(--color-paper)]/55 bg-[rgba(255,243,223,0.10)] text-[var(--color-paper)] transition hover:bg-[rgba(255,243,223,0.22)]"
        >
          <BackArrow />
        </a>
      )}
      <Postit tone="pink" rot={4} w={120} withPin onClick={onShowRules}>
        <div className="hand text-center text-[20px] text-ink leading-[1.1]">
          {t('briefing.tape.how_to_play_l1')}
          <br />
          {t('briefing.tape.how_to_play_l2')}
        </div>
      </Postit>
      {code && (
        <RoomCodePostit code={code} hidden={codeHidden} onToggleHidden={onToggleCodeHidden} />
      )}
      <div className="relative ml-auto h-12 max-w-[460px] flex-1">
        <EvidenceTape
          left={0}
          right={0}
          top={4}
          height={48}
          rot={-1.4}
          label={tapeLabel ?? t('briefing.tape.briefing_room')}
          labelSize={20}
          labelHeight={32}
        />
      </div>
    </header>
  );
};
